/**
 * Backoffice personas and default permission bundles (V43 rights management).
 * Preview bundles drive sidebar visibility only — route guards use real API permissions.
 */

export type Persona = 'Merchant' | 'Manager' | 'Worker' | 'Courier' | 'Admin' | 'Partner'

export const PARTNER_PERMISSIONS: readonly string[] = [
  'partner:orders:read',
  'partner:orders:write',
  'partner:rates:read',
  'partner:rates:write',
  'partner:zones:read',
  'partner:zones:write',
]

/** Shared + merchant surface (role_permissions for merchant). */
export const MERCHANT_PERMISSIONS: readonly string[] = [
  'orders:read',
  'orders:write',
  'bookings:read',
  'bookings:write',
  'shifts:read',
  'shifts:write',
  'stores:read',
  'schedule:generate',
  'catalog:read',
  'catalog:write',
  'stock:read',
  'stock:write',
  'stores:write',
  'workers:write',
  'managers:read',
  'managers:write',
  'company:read',
  'company:write',
  'credentials:read',
  'credentials:write',
  'media:upload',
  'media:generate',
  'reports:read',
  'reports:write',
  'plans:read',
  'plans:write',
  'assistant:use',
  'assistant:keys',
  'wallet:read',
  'wallet:write',
]

/**
 * Merchant minus company:write, credentials:write, workers:write.
 * Keeps managers:write so Team can appear when that grant is present on the member.
 */
export const MANAGER_PERMISSIONS: readonly string[] = MERCHANT_PERMISSIONS.filter(
  (p) =>
    p !== 'company:write' &&
    p !== 'credentials:write' &&
    p !== 'workers:write' &&
    p !== 'assistant:keys',
)

/** Worker member_permission_defaults. */
export const WORKER_PERMISSIONS: readonly string[] = [
  'orders:read',
  'orders:write',
  'bookings:read',
  'shifts:read',
  'stores:read',
  'workers:read',
]

export const COURIER_PERMISSIONS: readonly string[] = [
  'courier:orders:read',
  'courier:orders:write',
  'courier:location:write',
  'courier:availability:write',
]

export const ADMIN_PERMISSIONS: readonly string[] = [
  'admin:companies:read',
  'admin:companies:write',
  'admin:users:read',
  'admin:users:write',
  'admin:system:read',
  'admin:system:write',
  'admin:impersonate',
  'admin:finance:read',
  'admin:finance:write',
  'admin:support:read',
  'admin:support:write',
  'assistant:use',
]

export const PERSONA_BUNDLES: Record<Persona, readonly string[]> = {
  Merchant: MERCHANT_PERMISSIONS,
  Manager: MANAGER_PERMISSIONS,
  Worker: WORKER_PERMISSIONS,
  Courier: COURIER_PERMISSIONS,
  Admin: ADMIN_PERMISSIONS,
  Partner: PARTNER_PERMISSIONS,
}

export function isPersona(value: string | null | undefined): value is Persona {
  return (
    value === 'Merchant' ||
    value === 'Manager' ||
    value === 'Worker' ||
    value === 'Courier' ||
    value === 'Admin' ||
    value === 'Partner'
  )
}

export function heldPersonas(options: {
  companyRole: 'merchant' | 'manager' | 'worker' | null
  role: 'OWNER' | 'WORKER' | 'MANAGER' | 'NONE'
  isPlatformAdmin: boolean
  isCourier: boolean
  isCourierPartner?: boolean
}): Persona[] {
  const personas: Persona[] = []
  if (options.isPlatformAdmin) personas.push('Admin')
  if (options.companyRole === 'merchant' || options.role === 'OWNER') personas.push('Merchant')
  if (options.companyRole === 'manager' || options.role === 'MANAGER') personas.push('Manager')
  if (options.companyRole === 'worker' || (options.role === 'WORKER' && options.companyRole !== 'manager')) {
    personas.push('Worker')
  }
  if (options.isCourier) personas.push('Courier')
  if (options.isCourierPartner) personas.push('Partner')
  return personas
}

export function resolveActivePersona(options: {
  previewRole: string | null
  held: Persona[]
  companyRole: 'merchant' | 'manager' | 'worker' | null
  role: 'OWNER' | 'WORKER' | 'MANAGER' | 'NONE'
  isPlatformAdmin: boolean
  isCourier: boolean
  isCourierPartner?: boolean
}): Persona | 'None' {
  if (isPersona(options.previewRole) && options.held.includes(options.previewRole)) {
    return options.previewRole
  }
  if (options.isPlatformAdmin && options.held.includes('Admin') && !options.companyRole) {
    return 'Admin'
  }
  if (options.companyRole === 'merchant' || options.role === 'OWNER') return 'Merchant'
  if (options.companyRole === 'manager' || options.role === 'MANAGER') return 'Manager'
  if (options.companyRole === 'worker' || options.role === 'WORKER') return 'Worker'
  if (options.isCourier) return 'Courier'
  if (options.isCourierPartner) return 'Partner'
  if (options.isPlatformAdmin) return 'Admin'
  return 'None'
}

/**
 * Nav visibility checker: when previewing a held persona, intersect real perms
 * with that persona's default bundle so the sidebar shrinks without granting writes.
 */
export function navPermissionChecker(
  realPermissions: string[],
  activePersona: Persona | 'None',
  previewRole: string | null,
  held: Persona[],
): (code: string) => boolean {
  const realSet = new Set(realPermissions)
  const persona =
    isPersona(previewRole) && held.includes(previewRole)
      ? previewRole
      : activePersona

  if (persona !== 'None' && PERSONA_BUNDLES[persona]) {
    // Scope nav to the active persona bundle so dual-role users only see one surface.
    const bundle = new Set(PERSONA_BUNDLES[persona])
    return (code: string) => realSet.has(code) && bundle.has(code)
  }
  return (code: string) => realSet.has(code)
}
